import { useState } from "react";
import toast from "react-hot-toast";

import {
    useConfigNotifs, useCreateConfigNotif, useUpdateConfigNotif, useDeleteConfigNotif,
    useConfigServices, useCreateConfigServices, useUpdateConfigServices, useDeleteConfigServices,
    useConfigStatics, useCreateConfigStatic, useUpdateConfigStatic, useDeleteConfigStatic,
} from "@/services/configIntegrationService";
import type { ConfigNotif, ConfigServices, ConfigStatic } from "@/services/configIntegrationService";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit2, Trash2, Loader2, Search, KeyRound, CheckCircle2, XCircle } from "lucide-react";

// ============================================================================
// Helper kecil: badge status aktif & status password
// ============================================================================
function ActiveBadge({ active }: { active: boolean | number }) {
    return Boolean(active) ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle2 className="w-3 h-3" /> Aktif
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            <XCircle className="w-3 h-3" /> Nonaktif
        </span>
    );
}

function PasswordBadge({ isSet }: { isSet: boolean }) {
    return (
        <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${isSet ? "text-slate-500" : "text-rose-500"}`}>
            <KeyRound className="w-3 h-3" /> {isSet ? "Sudah diset" : "Belum diset"}
        </span>
    );
}

function useConfirm() {
    const [config, setConfig] = useState<{ title: string; description: string; action: () => void } | null>(null);
    const confirm = (title: string, description: string, action: () => void) => setConfig({ title, description, action });
    const dialog = (
        <AlertDialog open={!!config} onOpenChange={(open) => !open && setConfig(null)}>
            <AlertDialogContent className="rounded-3xl max-w-sm">
                <AlertDialogHeader>
                    <AlertDialogTitle>{config?.title}</AlertDialogTitle>
                    <AlertDialogDescription>{config?.description}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Batal</AlertDialogCancel>
                    <AlertDialogAction
                        className="bg-rose-600 hover:bg-rose-700"
                        onClick={() => { config?.action(); setConfig(null); }}
                    >
                        Hapus
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
    return { confirm, dialog };
}

// ============================================================================
// TAB 1: Config Notif (Notifikasi / OTP SSO Undiksha)
// ============================================================================
const emptyNotifForm = { system_code: "", notif_code: "", user: "", pass: "", url: "", is_active: true };

export function ConfigNotifTab() {
    const { data: rows = [], isLoading } = useConfigNotifs();
    const createMutation = useCreateConfigNotif();
    const updateMutation = useUpdateConfigNotif();
    const deleteMutation = useDeleteConfigNotif();
    const { confirm, dialog } = useConfirm();

    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [editing, setEditing] = useState<ConfigNotif | null>(null);
    const [form, setForm] = useState(emptyNotifForm);

    const filtered = rows.filter((r) =>
        [r.system_code, r.notif_code, r.user, r.url].join(" ").toLowerCase().includes(search.toLowerCase())
    );

    const openCreate = () => { setEditing(null); setForm(emptyNotifForm); setIsOpen(true); };
    const openEdit = (row: ConfigNotif) => {
        setEditing(row);
        setForm({ system_code: row.system_code, notif_code: row.notif_code, user: row.user, pass: "", url: row.url, is_active: Boolean(row.is_active) });
        setIsOpen(true);
    };

    const handleSubmit = () => {
        if (!form.system_code.trim() || !form.notif_code.trim() || !form.user.trim() || !form.url.trim()) {
            toast.error("Lengkapi field wajib terlebih dahulu");
            return;
        }
        const payload = { ...form, pass: form.pass || undefined };
        if (editing) {
            updateMutation.mutate({ id: editing.id_config_notif, payload }, {
                onSuccess: () => { toast.success("Konfigurasi notifikasi diperbarui"); setIsOpen(false); },
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal memperbarui konfigurasi"),
            });
        } else {
            if (!form.pass.trim()) { toast.error("Password wajib diisi untuk data baru"); return; }
            createMutation.mutate(payload, {
                onSuccess: () => { toast.success("Konfigurasi notifikasi ditambahkan"); setIsOpen(false); },
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal menambahkan konfigurasi"),
            });
        }
    };

    const handleDelete = (row: ConfigNotif) => {
        confirm("Hapus Konfigurasi", `Hapus konfigurasi notifikasi "${row.notif_code}"? Fitur yang bergantung padanya bisa berhenti berfungsi.`, () => {
            deleteMutation.mutate(row.id_config_notif, {
                onSuccess: () => toast.success("Konfigurasi dihapus"),
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal menghapus konfigurasi"),
            });
        });
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
                Kredensial layanan notifikasi/OTP dari SSO Undiksha (kirim notifikasi, verifikasi OTP, kirim email, dsb).
                Mengubah nilai ini mempengaruhi fitur yang sedang berjalan.
            </p>
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Cari kode/user/url..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-10 pl-9 pr-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>
                <Button onClick={openCreate} className="h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                    <Plus className="w-4 h-4 mr-1.5" /> Tambah Konfigurasi
                </Button>
            </div>

            <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-3">Kode Sistem</th>
                                <th className="px-4 py-3">Kode Notif</th>
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">URL</th>
                                <th className="px-4 py-3">Password</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {isLoading ? (
                                <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-indigo-500" /></td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={7} className="p-8 text-center text-slate-400">Belum ada data.</td></tr>
                            ) : filtered.map((row) => (
                                <tr key={row.id_config_notif} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20">
                                    <td className="px-4 py-3 font-semibold">{row.system_code}</td>
                                    <td className="px-4 py-3">{row.notif_code}</td>
                                    <td className="px-4 py-3 text-slate-500">{row.user}</td>
                                    <td className="px-4 py-3 max-w-[240px] truncate text-slate-500" title={row.url}>{row.url}</td>
                                    <td className="px-4 py-3"><PasswordBadge isSet={row.has_pass} /></td>
                                    <td className="px-4 py-3 text-center"><ActiveBadge active={row.is_active} /></td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1.5">
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => openEdit(row)}><Edit2 className="w-3.5 h-3.5" /></Button>
                                            <Button variant="outline" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(row)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader><DialogTitle>{editing ? "Edit Konfigurasi Notifikasi" : "Tambah Konfigurasi Notifikasi"}</DialogTitle></DialogHeader>
                    <div className="space-y-3 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Kode Sistem</label>
                                <Input value={form.system_code} onChange={(e) => setForm((v) => ({ ...v, system_code: e.target.value }))} placeholder="konaspi" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Kode Notif</label>
                                <Input value={form.notif_code} onChange={(e) => setForm((v) => ({ ...v, notif_code: e.target.value }))} placeholder="kirim_notif" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">URL Endpoint</label>
                            <Input value={form.url} onChange={(e) => setForm((v) => ({ ...v, url: e.target.value }))} placeholder="https://sso.undiksha.ac.id:8443/notifikasi/api/..." />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">User</label>
                                <Input value={form.user} onChange={(e) => setForm((v) => ({ ...v, user: e.target.value }))} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Password {editing && <span className="font-normal text-slate-400">(kosongkan jika tidak diubah)</span>}</label>
                                <Input type="password" value={form.pass} onChange={(e) => setForm((v) => ({ ...v, pass: e.target.value }))} placeholder={editing ? "••••••••" : ""} />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm pt-1">
                            <Switch checked={form.is_active} onCheckedChange={(c) => setForm((v) => ({ ...v, is_active: c }))} />
                            Aktifkan konfigurasi ini
                        </label>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setIsOpen(false)}>Batal</Button>
                            <Button onClick={handleSubmit} disabled={isSaving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {dialog}
        </div>
    );
}

// ============================================================================
// TAB 2: Config Services (Layanan Eksternal: DataCenter, SmartPayment, TTE, dst)
// ============================================================================
const emptyServicesForm = { services_login: "", services_url: "", services_data: "", services_username: "", services_password: "", services_id_pengguna: "", is_active: true };

export function ConfigServicesTab() {
    const { data: rows = [], isLoading } = useConfigServices();
    const createMutation = useCreateConfigServices();
    const updateMutation = useUpdateConfigServices();
    const deleteMutation = useDeleteConfigServices();
    const { confirm, dialog } = useConfirm();

    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [editing, setEditing] = useState<ConfigServices | null>(null);
    const [form, setForm] = useState(emptyServicesForm);

    const filtered = rows.filter((r) =>
        [r.services_data, r.services_login, r.services_url, r.services_username].join(" ").toLowerCase().includes(search.toLowerCase())
    );

    const openCreate = () => { setEditing(null); setForm(emptyServicesForm); setIsOpen(true); };
    const openEdit = (row: ConfigServices) => {
        setEditing(row);
        setForm({
            services_login: row.services_login,
            services_url: row.services_url,
            services_data: row.services_data,
            services_username: row.services_username,
            services_password: "",
            services_id_pengguna: row.services_id_pengguna || "",
            is_active: Boolean(row.is_active),
        });
        setIsOpen(true);
    };

    const handleSubmit = () => {
        if (!form.services_data.trim() || !form.services_login.trim() || !form.services_url.trim() || !form.services_username.trim()) {
            toast.error("Lengkapi field wajib terlebih dahulu");
            return;
        }
        const payload = { ...form, services_password: form.services_password || undefined };
        if (editing) {
            updateMutation.mutate({ id: editing.id_config_services, payload }, {
                onSuccess: () => { toast.success("Konfigurasi layanan diperbarui"); setIsOpen(false); },
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal memperbarui konfigurasi"),
            });
        } else {
            if (!form.services_password.trim()) { toast.error("Password wajib diisi untuk data baru"); return; }
            createMutation.mutate(payload, {
                onSuccess: () => { toast.success("Konfigurasi layanan ditambahkan"); setIsOpen(false); },
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal menambahkan konfigurasi"),
            });
        }
    };

    const handleDelete = (row: ConfigServices) => {
        confirm("Hapus Konfigurasi", `Hapus konfigurasi layanan "${row.services_data}"? Fitur yang bergantung padanya bisa berhenti berfungsi.`, () => {
            deleteMutation.mutate(row.id_config_services, {
                onSuccess: () => toast.success("Konfigurasi dihapus"),
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal menghapus konfigurasi"),
            });
        });
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
                Kredensial layanan eksternal (DataCenter Undiksha, SmartPayment, e-Proceeding, Akademik, TTE, dll).
                Data ini dipakai proses login SSO, pembayaran, dan penandatanganan sertifikat.
            </p>
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Cari kode layanan/url/username..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-10 pl-9 pr-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>
                <Button onClick={openCreate} className="h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                    <Plus className="w-4 h-4 mr-1.5" /> Tambah Layanan
                </Button>
            </div>

            <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px] sticky top-0">
                            <tr>
                                <th className="px-4 py-3">Kode Layanan</th>
                                <th className="px-4 py-3">URL Login</th>
                                <th className="px-4 py-3">URL Endpoint</th>
                                <th className="px-4 py-3">Username</th>
                                <th className="px-4 py-3">Password</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {isLoading ? (
                                <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-indigo-500" /></td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={7} className="p-8 text-center text-slate-400">Belum ada data.</td></tr>
                            ) : filtered.map((row) => (
                                <tr key={row.id_config_services} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20">
                                    <td className="px-4 py-3 font-semibold">{row.services_data}</td>
                                    <td className="px-4 py-3 max-w-[200px] truncate text-slate-500" title={row.services_login}>{row.services_login}</td>
                                    <td className="px-4 py-3 max-w-[200px] truncate text-slate-500" title={row.services_url}>{row.services_url}</td>
                                    <td className="px-4 py-3 max-w-[180px] truncate text-slate-500" title={row.services_username}>{row.services_username}</td>
                                    <td className="px-4 py-3"><PasswordBadge isSet={row.has_password} /></td>
                                    <td className="px-4 py-3 text-center"><ActiveBadge active={row.is_active} /></td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1.5">
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => openEdit(row)}><Edit2 className="w-3.5 h-3.5" /></Button>
                                            <Button variant="outline" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(row)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? "Edit Konfigurasi Layanan" : "Tambah Konfigurasi Layanan"}</DialogTitle></DialogHeader>
                    <div className="space-y-3 pt-2">
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Kode Layanan (identifier unik)</label>
                            <Input value={form.services_data} onChange={(e) => setForm((v) => ({ ...v, services_data: e.target.value }))} placeholder="mis. smartpayment-login" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">URL Login (untuk ambil token)</label>
                            <Input value={form.services_login} onChange={(e) => setForm((v) => ({ ...v, services_login: e.target.value }))} placeholder="https://..." />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">URL Endpoint</label>
                            <Input value={form.services_url} onChange={(e) => setForm((v) => ({ ...v, services_url: e.target.value }))} placeholder="https://..." />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Username</label>
                                <Input value={form.services_username} onChange={(e) => setForm((v) => ({ ...v, services_username: e.target.value }))} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Password {editing && <span className="font-normal text-slate-400">(kosongkan jika tidak diubah)</span>}</label>
                                <Input type="password" value={form.services_password} onChange={(e) => setForm((v) => ({ ...v, services_password: e.target.value }))} placeholder={editing ? "••••••••" : ""} />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">ID Pengguna (Opsional)</label>
                            <Textarea className="resize-none" value={form.services_id_pengguna} onChange={(e) => setForm((v) => ({ ...v, services_id_pengguna: e.target.value }))} />
                        </div>
                        <label className="flex items-center gap-2 text-sm pt-1">
                            <Switch checked={form.is_active} onCheckedChange={(c) => setForm((v) => ({ ...v, is_active: c }))} />
                            Aktifkan konfigurasi ini
                        </label>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setIsOpen(false)}>Batal</Button>
                            <Button onClick={handleSubmit} disabled={isSaving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {dialog}
        </div>
    );
}

// ============================================================================
// TAB 3: Config Static (Server Statis / FTP)
// ============================================================================
const emptyStaticForm = { hostname: "", username: "", password: "", default_dir: "", autoconnect: "", server: "", system: "", is_active: true };

export function ConfigStaticTab() {
    const { data: rows = [], isLoading } = useConfigStatics();
    const createMutation = useCreateConfigStatic();
    const updateMutation = useUpdateConfigStatic();
    const deleteMutation = useDeleteConfigStatic();
    const { confirm, dialog } = useConfirm();

    const [isOpen, setIsOpen] = useState(false);
    const [editing, setEditing] = useState<ConfigStatic | null>(null);
    const [form, setForm] = useState(emptyStaticForm);

    const openCreate = () => { setEditing(null); setForm(emptyStaticForm); setIsOpen(true); };
    const openEdit = (row: ConfigStatic) => {
        setEditing(row);
        setForm({
            hostname: row.hostname,
            username: row.username,
            password: "",
            default_dir: row.default_dir || "",
            autoconnect: row.autoconnect || "",
            server: row.server || "",
            system: row.system || "",
            is_active: Boolean(row.is_active),
        });
        setIsOpen(true);
    };

    const handleSubmit = () => {
        if (!form.hostname.trim() || !form.username.trim()) {
            toast.error("Lengkapi field wajib terlebih dahulu");
            return;
        }
        const payload = { ...form, password: form.password || undefined };
        if (editing) {
            updateMutation.mutate({ id: editing.id_config_static, payload }, {
                onSuccess: () => { toast.success("Konfigurasi server statis diperbarui"); setIsOpen(false); },
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal memperbarui konfigurasi"),
            });
        } else {
            if (!form.password.trim()) { toast.error("Password wajib diisi untuk data baru"); return; }
            createMutation.mutate(payload, {
                onSuccess: () => { toast.success("Konfigurasi server statis ditambahkan"); setIsOpen(false); },
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal menambahkan konfigurasi"),
            });
        }
    };

    const handleDelete = (row: ConfigStatic) => {
        confirm("Hapus Konfigurasi", `Hapus konfigurasi server "${row.hostname}"? Fitur yang bergantung padanya bisa berhenti berfungsi.`, () => {
            deleteMutation.mutate(row.id_config_static, {
                onSuccess: () => toast.success("Konfigurasi dihapus"),
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal menghapus konfigurasi"),
            });
        });
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
                Kredensial koneksi ke server penyimpanan statis (mis. untuk sinkronisasi file/foto).
            </p>
            <div className="flex justify-end">
                <Button onClick={openCreate} className="h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                    <Plus className="w-4 h-4 mr-1.5" /> Tambah Server
                </Button>
            </div>

            <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-3">Hostname</th>
                                <th className="px-4 py-3">Username</th>
                                <th className="px-4 py-3">Password</th>
                                <th className="px-4 py-3">Direktori</th>
                                <th className="px-4 py-3">Server</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {isLoading ? (
                                <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-indigo-500" /></td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={7} className="p-8 text-center text-slate-400">Belum ada data.</td></tr>
                            ) : rows.map((row) => (
                                <tr key={row.id_config_static} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20">
                                    <td className="px-4 py-3 font-semibold">{row.hostname}</td>
                                    <td className="px-4 py-3 text-slate-500">{row.username}</td>
                                    <td className="px-4 py-3"><PasswordBadge isSet={row.has_password} /></td>
                                    <td className="px-4 py-3 text-slate-500">{row.default_dir || "-"}</td>
                                    <td className="px-4 py-3 text-slate-500">{row.server || "-"}</td>
                                    <td className="px-4 py-3 text-center"><ActiveBadge active={row.is_active} /></td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1.5">
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => openEdit(row)}><Edit2 className="w-3.5 h-3.5" /></Button>
                                            <Button variant="outline" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(row)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader><DialogTitle>{editing ? "Edit Server Statis" : "Tambah Server Statis"}</DialogTitle></DialogHeader>
                    <div className="space-y-3 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Hostname</label>
                                <Input value={form.hostname} onChange={(e) => setForm((v) => ({ ...v, hostname: e.target.value }))} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Username</label>
                                <Input value={form.username} onChange={(e) => setForm((v) => ({ ...v, username: e.target.value }))} />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Password {editing && <span className="font-normal text-slate-400">(kosongkan jika tidak diubah)</span>}</label>
                            <Input type="password" value={form.password} onChange={(e) => setForm((v) => ({ ...v, password: e.target.value }))} placeholder={editing ? "••••••••" : ""} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Direktori Default</label>
                                <Input value={form.default_dir} onChange={(e) => setForm((v) => ({ ...v, default_dir: e.target.value }))} placeholder="/home/konaspi/" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Server</label>
                                <Input value={form.server} onChange={(e) => setForm((v) => ({ ...v, server: e.target.value }))} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">System</label>
                                <Input value={form.system} onChange={(e) => setForm((v) => ({ ...v, system: e.target.value }))} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Autoconnect</label>
                                <Input value={form.autoconnect} onChange={(e) => setForm((v) => ({ ...v, autoconnect: e.target.value }))} placeholder="true / false" />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm pt-1">
                            <Switch checked={form.is_active} onCheckedChange={(c) => setForm((v) => ({ ...v, is_active: c }))} />
                            Aktifkan konfigurasi ini
                        </label>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setIsOpen(false)}>Batal</Button>
                            <Button onClick={handleSubmit} disabled={isSaving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {dialog}
        </div>
    );
}
